import React from 'react';
import { Tag, Progress, Space, Typography, Badge, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  ClockCircleOutlined,
  WarningOutlined,
  DollarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Equipment } from '../../types';
import dayjs from 'dayjs';

const { Text } = Typography;

export const getEquipmentTableColumns = (): ColumnsType<Equipment> => [
  {
    title: 'Equipment Name',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    fixed: 'left',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (text: string, record: Equipment) => (
      <div>
        <Text strong>{text}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {record.brand} - {record.model}
        </Text>
      </div>
    ),
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    width: 100,
    filters: [
      { text: 'Cardio', value: 'Cardio' },
      { text: 'Strength', value: 'Strength' },
      { text: 'Functional', value: 'Functional' },
      { text: 'Accessories', value: 'Accessories' },
      { text: 'Technology', value: 'Technology' },
    ],
    onFilter: (value, record) => record.category === value,
    render: (category: string) => {
      const colorMap = {
        'Cardio': 'red',
        'Strength': 'blue',
        'Functional': 'green',
        'Accessories': 'orange',
        'Technology': 'purple',
      };
      return <Tag color={colorMap[category as keyof typeof colorMap]}>{category}</Tag>;
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    filters: [
      { text: 'Available', value: 'AVAILABLE' },
      { text: 'In Use', value: 'IN_USE' },
      { text: 'Maintenance', value: 'MAINTENANCE' },
      { text: 'Out of Order', value: 'OUT_OF_ORDER' },
      { text: 'Retired', value: 'RETIRED' },
    ],
    onFilter: (value, record) => record.status === value,
    render: (status: string) => {
      const statusConfig = {
        // Handle both backend enum values and frontend display values
        'AVAILABLE': { color: 'green', icon: <CheckCircleOutlined />, display: 'Available' },
        'Available': { color: 'green', icon: <CheckCircleOutlined />, display: 'Available' },
        'IN_USE': { color: 'blue', icon: <ClockCircleOutlined />, display: 'In Use' },
        'In Use': { color: 'blue', icon: <ClockCircleOutlined />, display: 'In Use' },
        'MAINTENANCE': { color: 'orange', icon: <ExclamationCircleOutlined />, display: 'Maintenance' },
        'Maintenance': { color: 'orange', icon: <ExclamationCircleOutlined />, display: 'Maintenance' },
        'OUT_OF_ORDER': { color: 'red', icon: <WarningOutlined />, display: 'Out of Order' },
        'Out of Order': { color: 'red', icon: <WarningOutlined />, display: 'Out of Order' },
        'RETIRED': { color: 'default', icon: <ExclamationCircleOutlined />, display: 'Retired' },
        'Retired': { color: 'default', icon: <ExclamationCircleOutlined />, display: 'Retired' },
      };
      
      const config = statusConfig[status as keyof typeof statusConfig] || 
                    { color: 'default', icon: <ExclamationCircleOutlined />, display: status };
      
      return (
        <Tag color={config.color} icon={config.icon}>
          {config.display}
        </Tag>
      );
    },
  },
  {
    title: 'Condition',
    dataIndex: 'condition',
    key: 'condition',
    width: 100,
    sorter: (a, b) => {
      const order = { 
        'EXCELLENT': 4, 'Excellent': 4, 
        'GOOD': 3, 'Good': 3, 
        'FAIR': 2, 'Fair': 2, 
        'POOR': 1, 'Poor': 1 
      };
      return (order[a.condition as keyof typeof order] || 0) - (order[b.condition as keyof typeof order] || 0);
    },
    render: (condition: string) => {
      const conditionConfig = {
        // Handle both backend enum values and frontend display values
        'EXCELLENT': { color: 'green', display: 'Excellent' },
        'Excellent': { color: 'green', display: 'Excellent' },
        'GOOD': { color: 'blue', display: 'Good' },
        'Good': { color: 'blue', display: 'Good' },
        'FAIR': { color: 'orange', display: 'Fair' },
        'Fair': { color: 'orange', display: 'Fair' },
        'POOR': { color: 'red', display: 'Poor' },
        'Poor': { color: 'red', display: 'Poor' },
      };
      
      const config = conditionConfig[condition as keyof typeof conditionConfig] || 
                    { color: 'default', display: condition };
      
      return <Tag color={config.color}>{config.display}</Tag>;
    },
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    width: 150,
    sorter: (a, b) => a.location.localeCompare(b.location),
    render: (location: string) => <Text>{location}</Text>,
  },
  {
    title: 'Utilization',
    dataIndex: 'utilization',
    key: 'utilization',
    width: 120,
    sorter: (a, b) => a.utilization - b.utilization,
    render: (utilization: number) => {
      const color = utilization >= 80 ? 'green' : utilization >= 60 ? 'orange' : 'red';
      return (
        <div>
          <Progress 
            percent={utilization} 
            size="small" 
            strokeColor={color}
            showInfo={false}
          />
          <Text style={{ fontSize: '12px' }}>{utilization}%</Text>
        </div>
      );
    },
  },
  {
    title: 'Users',
    key: 'users',
    width: 80,
    render: (_, record: Equipment) => (
      <Space>
        <Badge count={record.currentUsers} showZero />
        <Text type="secondary">/ {record.maxUsers}</Text>
      </Space>
    ),
  },
  {
    title: 'Value',
    key: 'value',
    width: 120,
    sorter: (a, b) => a.currentValue - b.currentValue,
    render: (_, record: Equipment) => (
      <div>
        <Space>
          <DollarOutlined style={{ color: '#52c41a' }} />
          <Text>${record.currentValue.toLocaleString()}</Text>
        </Space>
        <br />
        <Text type="secondary" style={{ fontSize: '11px' }}>
          ({((record.currentValue / record.acquisitionCost) * 100).toFixed(0)}% of original)
        </Text>
      </div>
    ),
  },
  {
    title: 'Maintenance',
    key: 'maintenance',
    width: 150,
    render: (_, record: Equipment) => {
      const nextMaintenance = dayjs(record.nextMaintenance);
      const isOverdue = nextMaintenance.isBefore(dayjs());
      const isDueSoon = nextMaintenance.isBefore(dayjs().add(7, 'days'));
      
      return (
        <div>
          <Space>
            <CalendarOutlined 
              style={{ 
                color: isOverdue ? '#f5222d' : isDueSoon ? '#faad14' : '#52c41a' 
              }} 
            />
            <Text 
              style={{ 
                color: isOverdue ? '#f5222d' : isDueSoon ? '#faad14' : 'inherit',
                fontSize: '12px'
              }}
            >
              {nextMaintenance.format('MMM DD')}
            </Text>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.maintenanceSchedule}
          </Text>
          {isOverdue && (
            <div>
              <Tag color="red" style={{ fontSize: '10px', lineHeight: '14px' }}>
                OVERDUE
              </Tag>
            </div>
          )}
          {!isOverdue && isDueSoon && (
            <div>
              <Tag color="orange" style={{ fontSize: '10px', lineHeight: '14px' }}>
                DUE SOON
              </Tag>
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: 'Warranty',
    dataIndex: 'warrantyExpiry',
    key: 'warranty',
    width: 100,
    render: (warrantyExpiry?: string) => {
      if (!warrantyExpiry) return <Text type="secondary">N/A</Text>;
      
      const warranty = dayjs(warrantyExpiry);
      const isExpired = warranty.isBefore(dayjs());
      const isExpiringSoon = warranty.isBefore(dayjs().add(30, 'days'));
      
      return (
        <div>
          <Text 
            style={{ 
              color: isExpired ? '#f5222d' : isExpiringSoon ? '#faad14' : 'inherit',
              fontSize: '12px'
            }}
          >
            {warranty.format('MMM DD, YYYY')}
          </Text>
          {isExpired && (
            <div>
              <Tag color="red" style={{ fontSize: '10px', lineHeight: '14px' }}>
                EXPIRED
              </Tag>
            </div>
          )}
          {!isExpired && isExpiringSoon && (
            <div>
              <Tag color="orange" style={{ fontSize: '10px', lineHeight: '14px' }}>
                EXPIRING
              </Tag>
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: 'Age',
    dataIndex: 'acquisitionDate',
    key: 'age',
    width: 80,
    sorter: (a, b) => dayjs(b.acquisitionDate).valueOf() - dayjs(a.acquisitionDate).valueOf(),
    render: (acquisitionDate: string) => {
      const age = dayjs().diff(dayjs(acquisitionDate), 'year', true);
      const ageColor = age < 1 ? '#52c41a' : age < 3 ? '#1890ff' : age < 7 ? '#faad14' : '#f5222d';
      
      return (
        <Text style={{ color: ageColor, fontSize: '12px' }}>
          {age < 1 ? `${Math.round(dayjs().diff(dayjs(acquisitionDate), 'month'))}mo` : `${Math.round(age)}y`}
        </Text>
      );
    },
  },
  {
    title: 'Serial #',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 120,
    render: (serialNumber: string) => (
      <Text code style={{ fontSize: '11px' }}>{serialNumber}</Text>
    ),
  },
]; 